import { Formik, Form } from 'formik'
import cn from 'classnames'

import { TimeRangeEnum } from '@interfaces/TimeRangeEnum'
import { Button } from '@components/Button'
import { SelectButtonField } from '@components/forms/SelectButtonField'
import { DateTimeInputField } from '@components/forms/DateTimeInputField'
import { usePermissionAction } from '@hooks/permission/usePermissionAction'
import { ExportButton } from '@components/ExportButton'
import { SalesReportSearchFormProps } from './interface'

export function SalesReportSearchForm({
  initialValues,
  handleSubmit,
  handleExport,
  handleReset,
}: SalesReportSearchFormProps) {
  const permission = usePermissionAction('sale')
  const timeRangeOption = [
    { value: TimeRangeEnum.DAY, text: 'วันนี้' },
    { value: TimeRangeEnum.MONTH, text: '1 เดือน' },
    // { value: TimeRangeEnum.YEAR, text: '1 ปี' },
  ]
  return (
    <>
      <Formik
        initialValues={initialValues}
        onSubmit={handleSubmit}
        onReset={handleReset}
      >
        {({ isSubmitting, values, setFieldValue, resetForm, submitForm }) => {
          // Calculate excluded dates for 90-day limit
          const getExcludedDates = (isStartDate: boolean) => {
            const excludedDates = []
            const maxDays = 90

            if (isStartDate && values.endAt) {
              // For start date, exclude dates that would make range > 90 days from endAt
              const endDate = new Date(values.endAt)

              // Calculate the valid range: 90 days before and after endAt
              const minValidDate = new Date(endDate)
              minValidDate.setDate(endDate.getDate() - maxDays)

              const maxValidDate = new Date(endDate)
              maxValidDate.setDate(endDate.getDate() + maxDays)

              // Exclude dates outside the valid range (past dates only)
              const today = new Date()
              for (let i = 0; i < 365; i += 1) {
                const testDate = new Date(today)
                testDate.setDate(today.getDate() - i)

                if (testDate < minValidDate || testDate > maxValidDate) {
                  excludedDates.push(new Date(testDate))
                }
              }
            } else if (!isStartDate && values.startAt) {
              // For end date, exclude dates that would make range > 90 days from startAt
              const startDate = new Date(values.startAt)

              // Calculate the valid range: 90 days before and after startAt
              const minValidDate = new Date(startDate)
              minValidDate.setDate(startDate.getDate() - maxDays)

              const maxValidDate = new Date(startDate)
              maxValidDate.setDate(startDate.getDate() + maxDays)

              // Exclude dates outside the valid range (past dates only)
              const today = new Date()
              for (let i = 0; i < 365; i += 1) {
                const testDate = new Date(today)
                testDate.setDate(today.getDate() - i)

                if (testDate < minValidDate || testDate > maxValidDate) {
                  excludedDates.push(new Date(testDate))
                }
              }
            }

            return excludedDates
          }

          return (
            <Form>
              <div className='flex justify-between items-end mb-[30px]'>
                <div className='flex items-end gap-x-[10px]'>
                  <SelectButtonField
                    name='timeRange'
                    options={timeRangeOption}
                    label='เลือกช่วงเวลา'
                    mode='line'
                    className='w-fit overflow-auto'
                    onChange={() => {
                      setFieldValue('startAt', undefined)
                      setFieldValue('endAt', undefined)
                    }}
                  />
                  <DateTimeInputField
                    className='w-[180px]'
                    name='startAt'
                    placeholder='ตั้งแต่วันที่'
                    onChange={value => {
                      setFieldValue('startAt', value)
                      if (values.timeRange) {
                        setFieldValue('timeRange', undefined)
                      }
                      // Reset endAt if the range exceeds 90 days
                      if (value && values.endAt) {
                        const startDate = new Date(value)
                        const endDate = new Date(values.endAt)
                        const diffTime = Math.abs(
                          endDate.getTime() - startDate.getTime()
                        )
                        const diffDays = Math.ceil(
                          diffTime / (1000 * 60 * 60 * 24)
                        )
                        if (diffDays > 90) {
                          setFieldValue('endAt', undefined)
                        }
                      }
                    }}
                    maxDate={values.endAt || new Date()}
                    excludeDates={getExcludedDates(true)}
                  />
                  <DateTimeInputField
                    className='w-[180px]'
                    name='endAt'
                    placeholder='ถึงวันที่'
                    onChange={value => {
                      setFieldValue('endAt', value)
                      if (values.timeRange) {
                        setFieldValue('timeRange', undefined)
                      }
                      // Reset startAt if the range exceeds 90 days
                      if (value && values.startAt) {
                        const startDate = new Date(values.startAt)
                        const endDate = new Date(value)
                        const diffTime = Math.abs(
                          endDate.getTime() - startDate.getTime()
                        )
                        const diffDays = Math.ceil(
                          diffTime / (1000 * 60 * 60 * 24)
                        )
                        if (diffDays > 90) {
                          setFieldValue('startAt', undefined)
                        }
                      }
                    }}
                    minDate={values.startAt}
                    maxDate={new Date()}
                    excludeDates={getExcludedDates(false)}
                  />
                </div>
                <div className='flex font-mitr justify-end ml-[25px]'>
                  <Button
                    type='button'
                    className='text-secondary underline'
                    onClick={() => {
                      resetForm()
                    }}
                  >
                    ล้างค่า
                  </Button>
                  <Button
                    type='submit'
                    className='bg-secondary text-white'
                    disabled={isSubmitting}
                  >
                    <span className='text-[14px]'>ค้นหา</span>
                  </Button>
                  <ExportButton
                    className={cn('ml-[20px]', {
                      hidden: !permission.download || !handleExport,
                    })}
                    handleClick={handleExport}
                  />
                </div>
              </div>
            </Form>
          )
        }}
      </Formik>
    </>
  )
}
